import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {FormArray, FormGroup} from "@angular/forms";
import {
  JaarrapportageEntity,
  ClientEntity,
  TrialBalanceItem, Jaarrapportage, Jaarrekening, BalanceItem, FormattedExplanation, BokkzJrLogic
} from "@snelstart-ventures/fiscaal/domain-logic";
import {BokkzAlgDataService, BokkzJrDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { combineLatest, distinctUntilChanged, Observable, ReplaySubject, Subject, takeUntil } from 'rxjs';
import { SpecFormattedexplanationComponent } from '../spec-formattedexplanation/spec-formattedexplanation.component';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SpecTrialbalanceComponent } from '../spec-trialbalance/spec-trialbalance.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-mva',
  templateUrl: './spec-mva.component.html',
  imports: [
    SpecFormattedexplanationComponent,
    SharedUiDetailsModule,
    SharedUiButtonModule,
    SharedUiFormControlsModule,
    SpecTrialbalanceComponent,
    CommonModule
  ],
  styleUrls: ['./spec-mva.component.scss']
})
export class SpecMvaComponent implements OnInit,OnChanges,OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabObservable!: Observable<number>;
  private selectedTabSubject = new ReplaySubject<number>(1);
  showCustomControls = true;
  private destroy$ = new Subject<void>();
  public selectedTab = 0;
  public index = 0;
  public dialogVisible = false;
  public client = {} as ClientEntity;
  public jrEntity = {} as JaarrapportageEntity;
  public isRechtspersoon = false;

  public propertyPlantEquipmentFg = {} as FormGroup;
  public landAndBuildingsFg = {} as FormGroup;
  public machineryFg = {} as FormGroup;
  public tangibleAssetsOtherFg = {} as FormGroup;
  public constructionInProgressPrepaymentsPropertyPlantEquipmentFg = {} as FormGroup;
  public propertyPlantEquipmentNotUsedInOperationsFg = {} as FormGroup;
  public propertyPlantEquipmentDisclosureFg = {} as FormGroup;
  public landAndBuildingsDisclosureFg = {} as FormGroup;
  public machineryDisclosureFg = {} as FormGroup;
  public tangibleAssetsOtherDisclosureFg = {} as FormGroup;
  public constructionInProgressPrepaymentsPropertyPlantEquipmentDisclosureFg = {} as FormGroup;
  public propertyPlantEquipmentNotUsedInOperationsDisclosureFg = {} as FormGroup;
  public propertyPlantEquipmentFinanceLeaseDisclosureFg = {} as FormGroup;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  public showDialog(input: number) {
    this.index = input
    this.dialogVisible = true;
  }

  constructor(
    public algDataService: BokkzAlgDataService,
    private cdRef: ChangeDetectorRef,
    private jrLogicService: BokkzJrLogic,
    public jrDataService: BokkzJrDataService) {
    this.createFormGroups();
  }

  ngOnInit(): void {
    if (this.selectedTabObservable) {
      this.selectedTabObservable
        .pipe(takeUntil(this.destroy$))
        .subscribe(tab => {
          this.selectedTabSubject.next(tab);
        });
    }

    combineLatest([
      this.selectedTabSubject.pipe(distinctUntilChanged()),
      this.jrDataService.jaarrapportageEntity
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tab, jrEntity]) => {
        this.selectedTab = tab;
        this.jrEntity = jrEntity;
        this.patchData(jrEntity);
      });

    this.jrDataService.isRechtspersoon.subscribe((value) => {
      this.isRechtspersoon = value;
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTabObservable']) {
      this.selectedTabObservable = changes['selectedTabObservable'].currentValue;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public createFormGroups() {
    this.propertyPlantEquipmentFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues();
    this.landAndBuildingsFg = this.jrDataService.createFormGroupBalanceItem();
    this.machineryFg = this.jrDataService.createFormGroupBalanceItem();
    this.tangibleAssetsOtherFg = this.jrDataService.createFormGroupBalanceItem();
    this.constructionInProgressPrepaymentsPropertyPlantEquipmentFg = this.jrDataService.createFormGroupBalanceItem();
    this.propertyPlantEquipmentNotUsedInOperationsFg = this.jrDataService.createFormGroupBalanceItem();
    this.propertyPlantEquipmentDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.landAndBuildingsDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.machineryDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.tangibleAssetsOtherDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.constructionInProgressPrepaymentsPropertyPlantEquipmentDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.propertyPlantEquipmentNotUsedInOperationsDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.propertyPlantEquipmentFinanceLeaseDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
  }

  private patchData(value: JaarrapportageEntity) {
    const materieleVasteActiva = value.rapportage?.jaarrekeningen?.at(0)?.balans?.materieleVasteActiva;

    if (materieleVasteActiva) {
      this.propertyPlantEquipmentFg.reset()

      if (materieleVasteActiva.propertyPlantEquipment) {
        this.propertyPlantEquipmentFg.patchValue(materieleVasteActiva.propertyPlantEquipment, {emitEvent: false})
      }

      const uitsplitsing = materieleVasteActiva.materieleVasteActivaUitsplitsing;
      const tekstueel = materieleVasteActiva.materieleVasteActivaTekstueel;

      if (uitsplitsing) {
        if (uitsplitsing.landAndBuildings) {
          const subItem = uitsplitsing.landAndBuildings;
          this.landAndBuildingsFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.landAndBuildingsFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing?.machinery) {
          const subItem = uitsplitsing.machinery;
          this.machineryFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.machineryFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing?.tangibleAssetsOther) {
          const subItem = uitsplitsing.tangibleAssetsOther;
          this.tangibleAssetsOtherFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.tangibleAssetsOtherFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing?.constructionInProgressPrepaymentsPropertyPlantEquipment) {
          const subItem = uitsplitsing.constructionInProgressPrepaymentsPropertyPlantEquipment;
          this.constructionInProgressPrepaymentsPropertyPlantEquipmentFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.constructionInProgressPrepaymentsPropertyPlantEquipmentFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing?.propertyPlantEquipmentNotUsedInOperations) {
          const subItem = uitsplitsing.propertyPlantEquipmentNotUsedInOperations;
          this.propertyPlantEquipmentNotUsedInOperationsFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.propertyPlantEquipmentNotUsedInOperationsFg.get('trialBalanceItems') as FormArray)
        }
      }
      if (tekstueel) {
        if (tekstueel.propertyPlantEquipmentDisclosure) {
          this.propertyPlantEquipmentDisclosureFg.patchValue(tekstueel.propertyPlantEquipmentDisclosure);
        }
        if (tekstueel.landAndBuildingsDisclosure) {
          this.landAndBuildingsDisclosureFg.patchValue(tekstueel.landAndBuildingsDisclosure);
        }
        if (tekstueel.machineryDisclosure) {
          this.machineryDisclosureFg.patchValue(tekstueel.machineryDisclosure);
        }
        if (tekstueel.tangibleAssetsOtherDisclosure) {
          this.tangibleAssetsOtherDisclosureFg.patchValue(tekstueel.tangibleAssetsOtherDisclosure);
        }
        if (tekstueel.constructionInProgressPrepaymentsPropertyPlantEquipmentDisclosure) {
          this.constructionInProgressPrepaymentsPropertyPlantEquipmentDisclosureFg.patchValue(tekstueel.constructionInProgressPrepaymentsPropertyPlantEquipmentDisclosure);
        }
        if (tekstueel.propertyPlantEquipmentNotUsedInOperationsDisclosure) {
          this.propertyPlantEquipmentNotUsedInOperationsDisclosureFg.patchValue(tekstueel.propertyPlantEquipmentNotUsedInOperationsDisclosure);
        }
        if (tekstueel.propertyPlantEquipmentFinanceLeaseDisclosure) {
          this.propertyPlantEquipmentFinanceLeaseDisclosureFg.patchValue(tekstueel.propertyPlantEquipmentFinanceLeaseDisclosure);
        }
      }
    }
  }

  updateTrialBalanceItems(lijst?: TrialBalanceItem[], formArray?: FormArray) {
    if (lijst && formArray) {
      for (let i = 0; i < lijst.length; i++) {
        if (i < formArray.length) {
          const control = formArray.at(i) as FormGroup;
          control.patchValue(lijst[i], {emitEvent: false});
        } else {
          const formGroup = this.jrDataService.createFormGroupTrialBalanceItem(lijst[i])
          formArray.push(formGroup, {emitEvent: false});
        }
      }
    }
  }

  saveForm(): void {
    this.showCustomControls = false;
    this.cdRef.detectChanges();
    setTimeout(() => {
      this.showCustomControls = true;
      this.cdRef.detectChanges();
    });
    this.propertyPlantEquipmentFg.updateValueAndValidity({ emitEvent: false });
    this.landAndBuildingsFg.updateValueAndValidity({ emitEvent: false });
    this.machineryFg.updateValueAndValidity({ emitEvent: false });
    this.tangibleAssetsOtherFg.updateValueAndValidity({ emitEvent: false });
    this.constructionInProgressPrepaymentsPropertyPlantEquipmentFg.updateValueAndValidity({ emitEvent: false });
    this.propertyPlantEquipmentNotUsedInOperationsFg.updateValueAndValidity({ emitEvent: false });
    this.propertyPlantEquipmentDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.landAndBuildingsDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.machineryDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.tangibleAssetsOtherDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.constructionInProgressPrepaymentsPropertyPlantEquipmentDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.propertyPlantEquipmentNotUsedInOperationsDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.propertyPlantEquipmentFinanceLeaseDisclosureFg.updateValueAndValidity({ emitEvent: false });

    if (this.jrEntity && this.jrEntity.rapportage && this.jrEntity.rapportage.jaarrekeningen) {
      this.jrEntity.rapportage.jaarrekeningen[0] = {
        ...this.jrEntity.rapportage.jaarrekeningen[0],
        balans: {
          ...this.jrEntity.rapportage.jaarrekeningen[0].balans,
          materieleVasteActiva: {
            ...this.jrEntity.rapportage.jaarrekeningen[0].balans?.materieleVasteActiva,
            materieleVasteActivaUitsplitsing: {
              ...this.jrEntity.rapportage.jaarrekeningen[0].balans?.materieleVasteActiva?.materieleVasteActivaUitsplitsing,
              landAndBuildings: this.landAndBuildingsFg.value,
              machinery: this.machineryFg.value,
              tangibleAssetsOther: this.tangibleAssetsOtherFg.value,
              constructionInProgressPrepaymentsPropertyPlantEquipment: this.constructionInProgressPrepaymentsPropertyPlantEquipmentFg.value,
              propertyPlantEquipmentNotUsedInOperations: this.propertyPlantEquipmentNotUsedInOperationsFg.value,
            },
            materieleVasteActivaTekstueel: {
              ...this.jrEntity.rapportage.jaarrekeningen[0].balans?.materieleVasteActiva?.materieleVasteActivaTekstueel,
              propertyPlantEquipmentDisclosure: this.propertyPlantEquipmentDisclosureFg.value,
              landAndBuildingsDisclosure: this.landAndBuildingsDisclosureFg.value,
              machineryDisclosure: this.machineryDisclosureFg.value,
              tangibleAssetsOtherDisclosure: this.tangibleAssetsOtherDisclosureFg.value,
              constructionInProgressPrepaymentsPropertyPlantEquipmentDisclosure: this.constructionInProgressPrepaymentsPropertyPlantEquipmentDisclosureFg.value,
              propertyPlantEquipmentNotUsedInOperationsDisclosure: this.propertyPlantEquipmentNotUsedInOperationsDisclosureFg.value,
              propertyPlantEquipmentFinanceLeaseDisclosure: this.propertyPlantEquipmentFinanceLeaseDisclosureFg.value,
            }
          }
        }
      } as Jaarrekening
    }

    this.jrEntity = {
      ...this.jrEntity,
      rapportage: {
        ...this.jrEntity.rapportage,
        jaarrekeningen: [this.jrEntity.rapportage?.jaarrekeningen?.at(0)]
      }
    } as Jaarrapportage
    this.jrDataService.updateEntity(this.jrEntity);
    this.jrLogicService.getCalculation(this.jrEntity);
  }

}
